import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import { fetchLecturers, Lecturer } from '../services/lecturerApi';
import { fetchLectures, Lecture } from '../services/lectureApi';
import { fetchLectureCategories, LectureCategory } from '../services/lectureCategoryApi';
import { fetchHomeCurations, HomeLectureSection } from '../services/homeCurationsApi';
import { fetchCoffeeChatList, CoffeeChatSection } from '../services/coffeeChatApi';
import { getCategoryImageWithFallback } from '../utils/imageUtils';
import { basicUiApi, Banner } from '../services/basicUiApi';
import { pickThumbnail } from '../utils/thumbnail';

// 데이터 검증 헬퍼 함수들
const validateLecturer = (lecturer: any): Lecturer => {
  return {
    ...lecturer,
    name: String(lecturer?.name || ''),
    introduction: String(lecturer?.introduction || ''),
    image_url: lecturer?.image_url ? String(lecturer.image_url) : null
  };
};

const validateLecture = (lecture: any): Lecture => {
  // 실제 데이터가 lecture_info 안에 있는 경우 처리
  const lectureInfo = lecture?.lecture_info || lecture;
  const lecturerInfo = lecture?.lecturer_info;
  
  // 디버깅을 위한 콘솔 로그
  // /// console.log('Raw lecture data:', lecture);
  // /// console.log('lectureInfo:', lectureInfo);
  // /// console.log('lecturerInfo:', lecturerInfo);
  // /// console.log('Original caption:', lectureInfo?.caption);
  
  // caption이 없거나 null인 경우 lecturer 정보로 대체
  let caption = String(lectureInfo?.caption || '');
  if (!caption || caption === 'null' || caption === '') {
    if (lecturerInfo) {
      caption = `${String(lecturerInfo.name || '')} · ${String(lecturerInfo.introduction || '')}`;
      // /// console.log('Generated caption from lecturer info:', caption);
    } else {
      caption = '강의 소개';
      // /// console.log('Using fallback caption:', caption);
    }
  }
  
  const validatedLecture = {
    id: lectureInfo?.id || lecture?.id || 0,
    title: String(lectureInfo?.title || ''),
    caption: caption,
    thumbnail_url: pickThumbnail(lectureInfo) || null,
    lecturer_info: lecturerInfo ? validateLecturer(lecturerInfo) : null
  };
  
  // /// console.log('Final validated lecture:', validatedLecture);
  return validatedLecture;
};

const validateCategory = (category: any): LectureCategory => {
  return {
    ...category,
    name: String(category?.name || ''),
    image_url: (category?.optimized_image_url || category?.image_url) ? String(category.optimized_image_url || category.image_url) : null,
    route_name: String(category?.route_name || '')
  };
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [classroomPage, setClassroomPage] = useState(0); // API는 0부터 시작으로 변경
  const [connectPage, setConnectPage] = useState(0); // API는 0부터 시작
  const [coffeeChatPage, setCoffeeChatPage] = useState(0); // 커피챗 페이지 상태 추가
  const [curationPages, setCurationPages] = useState<{ [key: number]: number }>({}); // 각 큐레이션별 현재 페이지 상태
  const [coffeeChatSectionPages, setCoffeeChatSectionPages] = useState<{ [key: number]: number }>({}); // 각 커피챗 섹션별 현재 페이지
  const [allLecturers, setAllLecturers] = useState<Lecturer[][]>([]); // 모든 페이지의 강연자 데이터
  const [allLectures, setAllLectures] = useState<Lecture[][]>([]); // 모든 페이지의 강의 데이터
  const [categories, setCategories] = useState<LectureCategory[]>([]); // 강의 카테고리 데이터
  const [homeCurations, setHomeCurations] = useState<HomeLectureSection[]>([]); // 홈 큐레이션 데이터
  const [coffeeChatSections, setCoffeeChatSections] = useState<CoffeeChatSection[]>([]); // 커피챗 섹션 데이터
  const [banners, setBanners] = useState<Banner[]>([]); // 배너 데이터
  const [lecturerTotalPages, setLecturerTotalPages] = useState(1);
  const [lectureTotalPages, setLectureTotalPages] = useState(1);
  const [coffeeChatTotalPages, setCoffeeChatTotalPages] = useState(1); // 커피챗 총 페이지 수 추가
  const [lectureLoading, setLectureLoading] = useState(false);
  const [lecturerLoading, setLecturerLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [curationsLoading, setCurationsLoading] = useState(false);
  const [coffeeChatLoading, setCoffeeChatLoading] = useState(false);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [imageLoadStates, setImageLoadStates] = useState<{ [key: number]: boolean }>({});
  
  // 드래그 상태 관리 - connect 섹션 추가
  const [classroomDrag, setClassroomDrag] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0,
    offset: 0,
    hasMoved: false,
    preventClick: false
  });
  
  const [connectDrag, setConnectDrag] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0,
    offset: 0,
    hasMoved: false,
    preventClick: false
  });
  
  const [coffeeChatDrag, setCoffeeChatDrag] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0,
    offset: 0,
    hasMoved: false,
    preventClick: false
  });
  
  // 큐레이션 드래그 상태 (각 큐레이션별로 분리)
  const [curationDrags, setCurationDrags] = useState<{ [key: number]: {
    isDragging: boolean;
    startX: number;
    currentX: number;
    offset: number;
    hasMoved: boolean;
    preventClick: boolean;
  }}>({});
  
  // 커피챗 섹션 드래그 상태 (각 섹션별로 분리)
  const [coffeeChatSectionDrags, setCoffeeChatSectionDrags] = useState<{ [key: number]: {
    isDragging: boolean;
    startX: number;
    currentX: number;
    offset: number;
    hasMoved: boolean;
    preventClick: boolean;
  }}>({});
  
  // 컨테이너 참조
  const classroomRef = useRef<HTMLDivElement>(null);
  const curationRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const coffeeChatSectionRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // 강의 카테고리 데이터 가져오기
  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await fetchLectureCategories();
      /// console.log('강의 카테고리 데이터 응답:', {response});
      if (response.code === 200 && Array.isArray(response.data)) {
        const validatedCategories = response.data.map(validateCategory);
        setCategories(validatedCategories);
        // /// console.log('강의 카테고리 데이터 로드 완료:', validatedCategories);
      }
    } catch (error) {
      /// console.error('강의 카테고리 데이터 로딩 실패:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // 홈 큐레이션 데이터 가져오기
  const loadHomeCurations = async () => {
    setCurationsLoading(true);
    try {
      const response = await fetchHomeCurations();
      /// console.log('홈 큐레이션 데이터 응답:', {response});
      if (response.code === 200 && Array.isArray(response.data)) {
        setHomeCurations(response.data);
        /// console.log('홈 큐레이션 데이터 로드 완료:', response.data);
      }
    } catch (error) {
      /// console.error('홈 큐레이션 데이터 로딩 실패:', error);
    } finally {
      setCurationsLoading(false);
    }
  };

  // 커피챗 데이터 가져오기
  const loadCoffeeChatSections = async () => {
    setCoffeeChatLoading(true);
    try {
      const response = await fetchCoffeeChatList();
      /// console.log('커피챗 데이터 응답:', {response});
      if (response.code === 200 && Array.isArray(response.data)) {
        // data 배열이 비어있으면 아무것도 표시하지 않음
        if (response.data.length === 0) {
          /// console.log('커피챗 데이터가 없음');
          setCoffeeChatSections([]);
          setCoffeeChatTotalPages(0);
        } else {
          setCoffeeChatSections(response.data);
          // 실제 데이터로 페이지 수 계산 (한 페이지에 4개씩)
          const totalPages = Math.ceil(response.data.length / 4);
          setCoffeeChatTotalPages(totalPages);
          /// console.log('커피챗 데이터 로드 완료:', response.data);
        }
      } 
    } catch (error) {
      /// console.error('커피챗 데이터 로딩 실패:', error);
      // setCoffeeChatSections(mockCoffeeChatData);
      // const totalPages = Math.ceil(mockCoffeeChatData.length / 4);
      // setCoffeeChatTotalPages(totalPages);
    } finally {
      setCoffeeChatLoading(false);
    }
  };

  // 모든 강의 데이터 가져오기 (최적화: 필요할 때만)
  const loadAllLectures = async () => {
    setLectureLoading(true);
    try {
      // 첫 번째 페이지로 총 페이지 수 확인
      const firstResponse = await fetchLectures({ page: 0, size: 3 });
      if (firstResponse.code === 200) {
        const totalPages = firstResponse.data.totalPages;
        setLectureTotalPages(totalPages);
        
        // 첫 3페이지만 미리 로드 (나머지는 필요할 때 로드)
        const allPages: Lecture[][] = [];
        const pagesToPreload = Math.min(3, totalPages);
        
        for (let page = 0; page < pagesToPreload; page++) {
          const response = await fetchLectures({ page, size: 3 });
          if (response.code === 200 && Array.isArray(response.data.content)) {
            allPages[page] = response.data.content.map(validateLecture);
          }
        }
        
        setAllLectures(allPages);
        /// console.log(`처음 ${pagesToPreload}개 강의 페이지 로드 완료:`, {allPages});
      }
    } catch (error) {
      /// console.error('강의 데이터 로딩 실패:', error);
    } finally {
      setLectureLoading(false);
    }
  };

  // 이미지 로딩 완료 핸들러
  const handleImageLoad = (lecturerId: number) => {
    setImageLoadStates(prev => ({
      ...prev,
      [lecturerId]: true
    }));
  };

  // 특정 페이지 지연 로딩
  const loadLecturerPageIfNeeded = async (page: number) => {
    if (allLecturers[page] || page >= lecturerTotalPages) return;
    
    try {
      const response = await fetchLecturers({ page, size: 4 });
      if (response.code === 200 && Array.isArray(response.data.content)) {
        const newLecturers = [...allLecturers];
        newLecturers[page] = response.data.content.map(validateLecturer);
        setAllLecturers(newLecturers);
        // /// console.log(`강연자 페이지 ${page} 지연 로드 완료`);
      }
    } catch (error) {
      /// console.error(`강연자 페이지 ${page} 지연 로딩 실패:`, error);
    }
  };

  const loadLecturePageIfNeeded = async (page: number) => {
    if (allLectures[page] || page >= lectureTotalPages) return;
    
    try {
      const response = await fetchLectures({ page, size: 3 });
      if (response.code === 200 && Array.isArray(response.data.content)) {
        const newLectures = [...allLectures];
        newLectures[page] = response.data.content.map(validateLecture);
        setAllLectures(newLectures);
        // /// console.log(`강의 페이지 ${page} 지연 로드 완료`);
      }
    } catch (error) {
      /// console.error(`강의 페이지 ${page} 지연 로딩 실패:`, error);
    }
  };

  const handleClassroomPageChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && classroomPage > 0) {
      const newPage = classroomPage - 1;
      setClassroomPage(newPage);
      // 필요한 페이지 지연 로딩
      loadLecturePageIfNeeded(newPage);
      if (newPage > 0) loadLecturePageIfNeeded(newPage - 1);
      if (newPage < lectureTotalPages - 1) loadLecturePageIfNeeded(newPage + 1);
    } else if (direction === 'next' && classroomPage < lectureTotalPages - 1) {
      const newPage = classroomPage + 1;
      setClassroomPage(newPage);
      // 필요한 페이지 지연 로딩
      loadLecturePageIfNeeded(newPage);
      if (newPage > 0) loadLecturePageIfNeeded(newPage - 1);
      if (newPage < lectureTotalPages - 1) loadLecturePageIfNeeded(newPage + 1);
    }
  };

  // 큐레이션 페이지 변경 함수
  const handleCurationPageChange = (curationIndex: number, direction: 'prev' | 'next') => {
    const curation = homeCurations[curationIndex];
    if (!curation) return;
    
    const currentPage = curationPages[curationIndex] || 0;
    const itemsPerPage = 3;
    const totalItems = curation.related_lectures.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    if (direction === 'prev' && currentPage > 0) {
      setCurationPages(prev => ({
        ...prev,
        [curationIndex]: currentPage - 1
      }));
    } else if (direction === 'next' && currentPage < totalPages - 1) {
      setCurationPages(prev => ({
        ...prev,
        [curationIndex]: currentPage + 1
      }));
    }
  };

  // 커피챗 섹션 페이지 변경 함수
  const handleCoffeeChatSectionPageChange = (sectionIndex: number, direction: 'prev' | 'next') => {
    const section = coffeeChatSections[sectionIndex];
    if (!section) return;
    
    const currentPage = coffeeChatSectionPages[sectionIndex] || 0;
    const itemsPerPage = 4; // 한 페이지에 4개씩
    const totalItems = section.related_persons.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    if (direction === 'prev' && currentPage > 0) {
      setCoffeeChatSectionPages(prev => ({
        ...prev,
        [sectionIndex]: currentPage - 1
      }));
    } else if (direction === 'next' && currentPage < totalPages - 1) {
      setCoffeeChatSectionPages(prev => ({
        ...prev,
        [sectionIndex]: currentPage + 1
      }));
    }
  };

  // 드래그 핸들러들 - 각 섹션별로 분리
  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent, section: 'classroom' | 'connect' | 'coffeeChat' | string) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    
    if (section === 'classroom') {
      setClassroomDrag({
        isDragging: true,
        startX: clientX,
        currentX: clientX,
        offset: 0,
        hasMoved: false,
        preventClick: false
      });
    } else if (section === 'connect') {
      setConnectDrag({
        isDragging: true,
        startX: clientX,
        currentX: clientX,
        offset: 0,
        hasMoved: false,
        preventClick: false
      });
    } else if (section === 'coffeeChat') {
      setCoffeeChatDrag({
        isDragging: true,
        startX: clientX,
        currentX: clientX,
        offset: 0,
        hasMoved: false,
        preventClick: false
      });
    } else if (section.startsWith('curation-')) {
      const curationIndex = parseInt(section.replace('curation-', ''));
      setCurationDrags(prev => ({
        ...prev,
        [curationIndex]: {
          isDragging: true,
          startX: clientX,
          currentX: clientX,
          offset: 0,
          hasMoved: false,
          preventClick: false
        }
      }));
    } else if (section.startsWith('coffee-chat-section-')) {
      const sectionIndex = parseInt(section.replace('coffee-chat-section-', ''));
      setCoffeeChatSectionDrags(prev => ({
        ...prev,
        [sectionIndex]: {
          isDragging: true,
          startX: clientX,
          currentX: clientX,
          offset: 0,
          hasMoved: false,
          preventClick: false
        }
      }));
    }
  }, []);

  const handleDragMove = useCallback((e: React.MouseEvent | React.TouchEvent, section: 'classroom' | 'connect' | 'coffeeChat' | string) => {
    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    
    if (section === 'classroom' && classroomDrag.isDragging) {
      const offset = clientX - classroomDrag.startX;
      const hasMoved = Math.abs(offset) > 5; // 5px 이상 움직이면 드래그로 간주
      
      setClassroomDrag(prev => ({
        ...prev,
        currentX: clientX,
        offset: offset,
        hasMoved: hasMoved || prev.hasMoved
      }));
    } else if (section === 'connect' && connectDrag.isDragging) {
      const offset = clientX - connectDrag.startX;
      const hasMoved = Math.abs(offset) > 5; // 5px 이상 움직이면 드래그로 간주
      
      setConnectDrag(prev => ({
        ...prev,
        currentX: clientX,
        offset: offset,
        hasMoved: hasMoved || prev.hasMoved
      }));
    } else if (section === 'coffeeChat' && coffeeChatDrag.isDragging) {
      const offset = clientX - coffeeChatDrag.startX;
      const hasMoved = Math.abs(offset) > 5;
      
      setCoffeeChatDrag(prev => ({
        ...prev,
        currentX: clientX,
        offset: offset,
        hasMoved: hasMoved || prev.hasMoved
      }));
    } else if (section.startsWith('curation-')) {
      const curationIndex = parseInt(section.replace('curation-', ''));
      const curationDrag = curationDrags[curationIndex];
      if (curationDrag && curationDrag.isDragging) {
        const offset = clientX - curationDrag.startX;
        const hasMoved = Math.abs(offset) > 5;
        
        setCurationDrags(prev => ({
          ...prev,
          [curationIndex]: {
            ...prev[curationIndex],
            currentX: clientX,
            offset: offset,
            hasMoved: hasMoved || prev[curationIndex].hasMoved
          }
        }));
      }
    } else if (section.startsWith('coffee-chat-section-')) {
      const sectionIndex = parseInt(section.replace('coffee-chat-section-', ''));
      const sectionDrag = coffeeChatSectionDrags[sectionIndex];
      if (sectionDrag && sectionDrag.isDragging) {
        const offset = clientX - sectionDrag.startX;
        const hasMoved = Math.abs(offset) > 5;
        
        setCoffeeChatSectionDrags(prev => ({
          ...prev,
          [sectionIndex]: {
            ...prev[sectionIndex],
            currentX: clientX,
            offset: offset,
            hasMoved: hasMoved || prev[sectionIndex].hasMoved
          }
        }));
      }
    }
  }, [classroomDrag.isDragging, classroomDrag.startX, connectDrag.isDragging, connectDrag.startX, coffeeChatDrag.isDragging, coffeeChatDrag.startX, curationDrags, coffeeChatSectionDrags]);

  const handleDragEnd = useCallback((section: 'classroom' | 'connect' | string) => {
    if (section === 'classroom' && classroomDrag.isDragging) {
      const dragDistance = classroomDrag.currentX - classroomDrag.startX;
      const threshold = 100;
      
      if (dragDistance > threshold && classroomPage > 0) {
        const newPage = classroomPage - 1;
        setClassroomPage(newPage);
        loadLecturePageIfNeeded(newPage);
      } else if (dragDistance < -threshold && classroomPage < lectureTotalPages - 1) {
        const newPage = classroomPage + 1;
        setClassroomPage(newPage);
        loadLecturePageIfNeeded(newPage);
      }
      
      const shouldPreventClick = classroomDrag.hasMoved;
      
      setClassroomDrag({
        isDragging: false,
        startX: 0,
        currentX: 0,
        offset: 0,
        hasMoved: false,
        preventClick: shouldPreventClick
      });
      
      // 드래그 후 200ms 동안 클릭 차단
      if (shouldPreventClick) {
        setTimeout(() => {
          setClassroomDrag(prev => ({ ...prev, preventClick: false }));
        }, 200);
      }
    } else if (section === 'connect' && connectDrag.isDragging) {
      const dragDistance = connectDrag.currentX - connectDrag.startX;
      const threshold = 100;
      
      if (dragDistance > threshold && connectPage > 0) {
        const newPage = connectPage - 1;
        setConnectPage(newPage);
        loadLecturerPageIfNeeded(newPage);
      } else if (dragDistance < -threshold && connectPage < lecturerTotalPages - 1) {
        const newPage = connectPage + 1;
        setConnectPage(newPage);
        loadLecturerPageIfNeeded(newPage);
      }
      
      const shouldPreventClick = connectDrag.hasMoved;
      
      setConnectDrag({
        isDragging: false,
        startX: 0,
        currentX: 0,
        offset: 0,
        hasMoved: false,
        preventClick: shouldPreventClick
      });
      
      // 드래그 후 200ms 동안 클릭 차단
      if (shouldPreventClick) {
        setTimeout(() => {
          setConnectDrag(prev => ({ ...prev, preventClick: false }));
        }, 200);
      }
    } else if (section.startsWith('curation-')) {
      const curationIndex = parseInt(section.replace('curation-', ''));
      const curationDrag = curationDrags[curationIndex];
      
      if (curationDrag && curationDrag.isDragging) {
        const dragDistance = curationDrag.currentX - curationDrag.startX;
        const threshold = 100;
        const currentPage = curationPages[curationIndex] || 0;
        const curation = homeCurations[curationIndex];
        
        if (curation) {
          const itemsPerPage = 3;
          const totalItems = curation.related_lectures.length;
          const totalPages = Math.ceil(totalItems / itemsPerPage);
          
          if (dragDistance > threshold && currentPage > 0) {
            handleCurationPageChange(curationIndex, 'prev');
          } else if (dragDistance < -threshold && currentPage < totalPages - 1) {
            handleCurationPageChange(curationIndex, 'next');
          }
        }
        
        const shouldPreventClick = curationDrag.hasMoved;
        
        setCurationDrags(prev => ({
          ...prev,
          [curationIndex]: {
            isDragging: false,
            startX: 0,
            currentX: 0,
            offset: 0,
            hasMoved: false,
            preventClick: shouldPreventClick
          }
        }));
        
        if (shouldPreventClick) {
          setTimeout(() => {
            setCurationDrags(prev => ({
              ...prev,
              [curationIndex]: {
                ...prev[curationIndex],
                preventClick: false
              }
            }));
          }, 200);
        }
      }
    } else if (section.startsWith('coffee-chat-section-')) {
      const sectionIndex = parseInt(section.replace('coffee-chat-section-', ''));
      const sectionDrag = coffeeChatSectionDrags[sectionIndex];
      
      if (sectionDrag && sectionDrag.isDragging) {
        const dragDistance = sectionDrag.currentX - sectionDrag.startX;
        const threshold = 100;
        const currentPage = coffeeChatSectionPages[sectionIndex] || 0;
        const section = coffeeChatSections[sectionIndex];
        
        if (section) {
          const itemsPerPage = 4;
          const totalItems = section.related_persons.length;
          const totalPages = Math.ceil(totalItems / itemsPerPage);
          
          if (dragDistance > threshold && currentPage > 0) {
            handleCoffeeChatSectionPageChange(sectionIndex, 'prev');
          } else if (dragDistance < -threshold && currentPage < totalPages - 1) {
            handleCoffeeChatSectionPageChange(sectionIndex, 'next');
          }
        }
        
        const shouldPreventClick = sectionDrag.hasMoved;
        
        setCoffeeChatSectionDrags(prev => ({
          ...prev,
          [sectionIndex]: {
            isDragging: false,
            startX: 0,
            currentX: 0,
            offset: 0,
            hasMoved: false,
            preventClick: shouldPreventClick
          }
        }));
        
        if (shouldPreventClick) {
          setTimeout(() => {
            setCoffeeChatSectionDrags(prev => ({
              ...prev,
              [sectionIndex]: {
                ...prev[sectionIndex],
                preventClick: false
              }
            }));
          }, 200);
        }
      }
    }
  }, [classroomDrag, connectDrag, classroomPage, connectPage, lectureTotalPages, lecturerTotalPages, loadLecturePageIfNeeded, loadLecturerPageIfNeeded, curationDrags, curationPages, homeCurations, coffeeChatSectionDrags, coffeeChatSectionPages, coffeeChatSections]);

  // 배너 슬라이드 상태 관리
  const [bannerPage, setBannerPage] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // 배너 드래그 상태 관리
  const [bannerDrag, setBannerDrag] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0,
    offset: 0,
    hasMoved: false,
    preventClick: false
  });

  // 동적 배너 데이터 계산
  const totalBannersCount = banners.length;
  
  // 무한 슬라이드를 위한 확장된 배너 데이터
  const extendedBannerData = totalBannersCount > 0 ? [
    banners[banners.length - 1], // 마지막 배너를 앞에 추가
    ...banners,
    banners[0] // 첫 번째 배너를 뒤에 추가
  ] : [];

  // 현재 실제 페이지 번호 계산
  const getCurrentBannerPage = () => {
    if (bannerPage === 0) return totalBannersCount;
    if (bannerPage === totalBannersCount + 1) return 1;
    return bannerPage;
  };

  // 배너 페이지 변경 핸들러 (무한 슬라이드) - 배너가 2개 이상일 때만
  const handleBannerPageChange = useCallback((direction: 'prev' | 'next') => {
    if (totalBannersCount <= 1) return; // 배너가 1개 이하면 페이지 변경 비활성화
    
    setIsTransitioning(true);
    
    if (direction === 'prev') {
      if (bannerPage === 1) {
        setBannerPage(bannerPage - 1); // 0으로 이동 (복사본)
      } else {
        setBannerPage(bannerPage - 1);
      }
    } else {
      if (bannerPage === totalBannersCount) {
        setBannerPage(bannerPage + 1); // totalBanners + 1로 이동 (복사본)
      } else {
        setBannerPage(bannerPage + 1);
      }
    }
  }, [bannerPage, totalBannersCount]);

  // 배너 드래그 핸들러
  const handleBannerDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (totalBannersCount <= 1) return; // 배너가 1개 이하면 드래그 비활성화
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    
    setBannerDrag({
      isDragging: true,
      startX: clientX,
      currentX: clientX,
      offset: 0,
      hasMoved: false,
      preventClick: false
    });
  }, [totalBannersCount]);

  const handleBannerDragMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    
    if (bannerDrag.isDragging) {
      const offset = clientX - bannerDrag.startX;
      const hasMoved = Math.abs(offset) > 5; // 5px 이상 움직이면 드래그로 간주
      
      setBannerDrag(prev => ({
        ...prev,
        currentX: clientX,
        offset: offset,
        hasMoved: hasMoved || prev.hasMoved
      }));
    }
  }, [bannerDrag.isDragging, bannerDrag.startX]);

  const handleBannerDragEnd = useCallback(() => {
    if (bannerDrag.isDragging && totalBannersCount > 1) {
      const dragDistance = bannerDrag.currentX - bannerDrag.startX;
      const threshold = 100;
      
      if (dragDistance > threshold) {
        handleBannerPageChange('prev');
      } else if (dragDistance < -threshold) {
        handleBannerPageChange('next');
      }
      
      const shouldPreventClick = bannerDrag.hasMoved;
      
      setBannerDrag({
        isDragging: false,
        startX: 0,
        currentX: 0,
        offset: 0,
        hasMoved: false,
        preventClick: shouldPreventClick
      });
      
      // 드래그 후 200ms 동안 클릭 차단
      if (shouldPreventClick) {
        setTimeout(() => {
          setBannerDrag(prev => ({ ...prev, preventClick: false }));
        }, 200);
      }
    }
  }, [bannerDrag, totalBannersCount, handleBannerPageChange]);

  // 배너 데이터 로드 함수
  const loadBanners = useCallback(async () => {
    setBannerLoading(true);
    try {
      const bannerData = await basicUiApi.getBanners();
      setBanners(bannerData);
    } catch (error) {
      /// console.error('배너 로딩 실패:', error);
    } finally {
      setBannerLoading(false);
    }
  }, []);

  // 자동 슬라이드 효과 (15초마다) - 배너가 2개 이상일 때만
  useEffect(() => {
    if (totalBannersCount <= 1) return; // 배너가 1개 이하면 자동 슬라이드 비활성화
    
    const interval = setInterval(() => {
      if (!bannerDrag.isDragging) {
        handleBannerPageChange('next');
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [bannerDrag.isDragging, handleBannerPageChange, totalBannersCount]);

  // 무한 슬라이드 처리를 위한 useEffect - 배너가 2개 이상일 때만
  useEffect(() => {
    if (totalBannersCount <= 1) return; // 배너가 1개 이하면 무한 슬라이드 비활성화
    
    if (bannerPage === 0) {
      // 첫 번째 복사본에서 실제 마지막으로 이동
      setTimeout(() => {
        setIsTransitioning(false);
        setBannerPage(totalBannersCount);
      }, 600);
    } else if (bannerPage === totalBannersCount + 1) {
      // 마지막 복사본에서 실제 첫 번째로 이동
      setTimeout(() => {
        setIsTransitioning(false);
        setBannerPage(1);
      }, 600);
    }
  }, [bannerPage, totalBannersCount]);

  // 컴포넌트 마운트 시 초기 데이터 로드
  useEffect(() => {
    loadCategories();
    loadHomeCurations(); // 홈 큐레이션 데이터 로드
    loadCoffeeChatSections(); // 커피챗 데이터 로드
    loadBanners(); // 배너 데이터 로드
    loadAllLectures();  // 처음 3페이지만 로드
  }, [loadBanners]);

  // 카테고리 카드 클릭 핸들러
  const handleCategoryClick = (routeName: string) => {
    navigate(`/class/category?category=${routeName}`);
  };

  // 강연자 카드 클릭 핸들러 (드래그와 구분)
  const handleLecturerCardClick = (lecturerId: number, e: React.MouseEvent) => {
    // 드래그가 발생했거나 클릭이 차단된 상태라면 클릭 이벤트 무시
    if (connectDrag.hasMoved || connectDrag.preventClick || connectDrag.isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // /// console.log('강연자 클릭:', lecturerId);
    navigate(`/coffee-chat/${lecturerId}`);
  };

  // 강의 카드 클릭 핸들러 (드래그와 구분)
  const handleLectureCardClick = (lectureId: number, e: React.MouseEvent) => {
    // 드래그가 발생했거나 클릭이 차단된 상태라면 클릭 이벤트 무시
    if (classroomDrag.hasMoved || classroomDrag.preventClick || classroomDrag.isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // /// console.log('강의 클릭:', lectureId);
    navigate(`/class/lecture/${lectureId}`);
  };

  // 큐레이션 카드 클릭 핸들러 (드래그와 구분)
  const handleCurationCardClick = (lectureId: number, curationIndex: number, e: React.MouseEvent) => {
    const curationDrag = curationDrags[curationIndex];
    if (curationDrag && (curationDrag.hasMoved || curationDrag.preventClick || curationDrag.isDragging)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    navigate(`/class/lecture/${lectureId}`);
  };

  // 커피챗 person 클릭 핸들러 (드래그와 구분)
  const handleCoffeeChatPersonClick = (personId: number, sectionIndex: number, e: React.MouseEvent) => {
    const sectionDrag = coffeeChatSectionDrags[sectionIndex];
    if (sectionDrag && (sectionDrag.hasMoved || sectionDrag.preventClick || sectionDrag.isDragging)) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    navigate(`/coffee-chat/${personId}`);
  };

  return (
    <Layout>
      <Container>
        {/* 배너 섹션 */}
        {bannerLoading ? (
          <BannerSection>
            <BannerSlideWrapper>
              <BannerSlide style={{ width: '100%' }}>
                <BannerContent>
                  <BannerTitle>로딩 중...</BannerTitle>
                </BannerContent>
              </BannerSlide>
            </BannerSlideWrapper>
          </BannerSection>
        ) : banners.length > 0 ? (
          <BannerSection>
            <BannerSlideWrapper 
              onMouseDown={handleBannerDragStart}
              onMouseMove={handleBannerDragMove}
              onTouchStart={handleBannerDragStart}
              onTouchMove={handleBannerDragMove}
              onMouseUp={handleBannerDragEnd}
              onTouchEnd={handleBannerDragEnd}
              onMouseLeave={handleBannerDragEnd}
              style={{ 
                transform: `translateX(calc(-${(bannerPage) * (100/(totalBannersCount + 2))}% + ${bannerDrag.isDragging ? bannerDrag.offset : 0}px))`,
                transition: bannerDrag.isDragging || !isTransitioning ? 'none' : 'transform 0.6s ease',
                width: `${(totalBannersCount + 2) * 100}%`
              }}
            >
              {extendedBannerData.map((banner, index) => (
                <BannerSlide key={`${banner.id}-${index}`} style={{ width: `${100/(totalBannersCount + 2)}%` }}>
                  <BannerSlideContent $backgroundImage={banner.image_url}>
                    <BannerContent>
                      <BannerTitle $fontColor={banner.font_color_hex_code}>{banner.title}</BannerTitle>
                      <BannerSubtitle $fontColor={banner.font_color_hex_code}>{banner.text}</BannerSubtitle>
                    </BannerContent>
                  </BannerSlideContent>
                </BannerSlide>
              ))}
            </BannerSlideWrapper>
            
            {/* 페이지네이션 인디케이터 - 배너가 2개 이상일 때만 표시 */}
            {totalBannersCount > 1 && (
              <BannerPagination>
                {banners.map((_, index) => (
                  <PaginationDot 
                    key={index}
                    $active={getCurrentBannerPage() === index + 1}
                    onClick={() => {
                      setIsTransitioning(true);
                      setBannerPage(index + 1);
                    }}
                  />
                ))}
              </BannerPagination>
            )}
          </BannerSection>
        ) : null}

        {/* 메인 콘텐츠 컨테이너 */}
        <MainContainer>
          {/* EXPLORE THE CAMPUS 섹션 */}
          <Section>
            <SectionHeader>
              <SectionTitleGroup>
                <SectionTitle>캠퍼스 강의실 둘러보기</SectionTitle>
                <SectionCaption>의료 AI 강의를 카테고리별로 자유롭게 탐색하며, 관심 있는 수업을 찾아보세요.</SectionCaption>
              </SectionTitleGroup>
            </SectionHeader>
            <CategoryGrid>
              {categoriesLoading ? (
                // 스켈레톤 UI - 6개 카드 (텍스트 부분 제거)
                Array.from({ length: 6 }).map((_, index) => (
                  <CategoryCard key={`skeleton-${index}`} style={{ pointerEvents: 'none' }}>
                    <CategoryCardImage>
                      <SkeletonBackground />
                    </CategoryCardImage>
                  </CategoryCard>
                ))
              ) : (
                categories.map((category) => (
                  <CategoryCard 
                    key={category.id} 
                    onClick={() => handleCategoryClick(category.route_name)}
                  >
                    <CategoryCardImage 
                      style={{
                        backgroundImage: `url('${getCategoryImageWithFallback(category, 400, 400)}')`
                      }} 
                    />
                    <CategoryCardOverlay>
                      <CategoryCardTitle>{String(category.name || '')}</CategoryCardTitle>
                    </CategoryCardOverlay>
                  </CategoryCard>
                ))
              )}
            </CategoryGrid>
          </Section>

          {/* VISIT THE CLASSROOM 섹션 - 큐레이션 데이터 사용 */}
          {homeCurations.map((curation, curationIndex) => {
            const currentPage = curationPages[curationIndex] || 0;
            const itemsPerPage = 3;
            const totalItems = curation.related_lectures.length;
            const totalPages = Math.ceil(totalItems / itemsPerPage);
            const curationDrag = curationDrags[curationIndex] || {
              isDragging: false,
              startX: 0,
              currentX: 0,
              offset: 0,
              hasMoved: false,
              preventClick: false
            };
            
            return (
              <Section key={curation.home_lecture_section_id}>
                <SectionHeader>
                  <SectionTitleGroup>
                    <SectionTitle>{curation.title}</SectionTitle>
                    <SectionCaption>{curation.text}</SectionCaption>
                  </SectionTitleGroup>
                  {totalPages > 1 && (
                    <SectionTrailing>
                      <PaginationButtons>
                        <PaginationContainer>
                          <PaginationContent>
                            <PaginationBtn 
                              onClick={() => handleCurationPageChange(curationIndex, 'prev')}
                              disabled={currentPage === 0}
                            >
                              <PaginationIcon isActive={currentPage > 0}>
                                <img src="/images/left.svg" alt="Previous" />
                              </PaginationIcon>
                            </PaginationBtn>
                            <PaginationBtn 
                              onClick={() => handleCurationPageChange(curationIndex, 'next')}
                              disabled={currentPage >= totalPages - 1}
                            >
                              <PaginationIcon isRight isActive={currentPage < totalPages - 1}>
                                <img src="/images/left.svg" alt="Next" />
                              </PaginationIcon>
                            </PaginationBtn>
                          </PaginationContent>
                        </PaginationContainer>
                      </PaginationButtons>
                    </SectionTrailing>
                  )}
                </SectionHeader>
                <SlideContainer
                  ref={(el) => { curationRefs.current[curationIndex] = el; }}
                  onMouseDown={(e) => handleDragStart(e, `curation-${curationIndex}`)}
                  onMouseMove={(e) => handleDragMove(e, `curation-${curationIndex}`)}
                  onTouchStart={(e) => handleDragStart(e, `curation-${curationIndex}`)}
                  onTouchMove={(e) => handleDragMove(e, `curation-${curationIndex}`)}
                  onMouseUp={() => handleDragEnd(`curation-${curationIndex}`)}
                  onTouchEnd={() => handleDragEnd(`curation-${curationIndex}`)}
                  onMouseLeave={() => handleDragEnd(`curation-${curationIndex}`)}
                  style={{ 
                    pointerEvents: curationDrag.isDragging && curationDrag.hasMoved ? 'none' : 'auto',
                    userSelect: 'none'
                  }}
                >
                  {curationsLoading ? (
                    // 스켈레톤 UI
                    <LectureGrid>
                      {Array.from({ length: 3 }).map((_, index) => (
                        <LectureCard 
                          key={`skeleton-${index}`}
                          style={{ pointerEvents: 'none' }}
                        >
                          <LectureCardImage>
                            <SkeletonBackground />
                            <LectureCardOverlay>
                              <LectureCardContent>
                                <SkeletonTitle />
                                <SkeletonSubtitle />
                              </LectureCardContent>
                            </LectureCardOverlay>
                          </LectureCardImage>
                        </LectureCard>
                      ))}
                    </LectureGrid>
                  ) : totalPages > 1 ? (
                    <SlideWrapper style={{ 
                      transform: `translateX(calc(-${currentPage * (100/totalPages)}% - ${currentPage * 24}px + ${curationDrag.isDragging ? curationDrag.offset : 0}px))`,
                      transition: curationDrag.isDragging ? 'none' : 'transform 0.6s ease',
                      width: `${totalPages * 100}%`
                    }}>
                      {Array.from({ length: totalPages }).map((_, pageIndex) => (
                        <SlidePage key={pageIndex} style={{ width: `${100/totalPages}%` }}>
                          <LectureGrid>
                            {curation.related_lectures
                              .slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage)
                              .map((lecture) => (
                                <LectureCard 
                                  key={lecture.relation_id}
                                  onClick={(e) => handleCurationCardClick(lecture.lecture_id, curationIndex, e)}
                                >
                                  <LectureCardImage>
                                    <LectureCardBg style={{backgroundImage: lecture.thumbnail_url ? `url('${lecture.thumbnail_url}')` : 'none'}} />
                                    <LectureCardOverlay>
                                      <LectureCardContent>
                                        <LectureCardTitle>{lecture.title}</LectureCardTitle>
                                        <LectureCardSubtitle>{lecture.lecturer_info.name} · {lecture.lecturer_info.department}</LectureCardSubtitle>
                                      </LectureCardContent>
                                    </LectureCardOverlay>
                                  </LectureCardImage>
                                </LectureCard>
                              ))}
                            {/* 빈 공간 채우기 */}
                            {Array.from({ 
                              length: Math.max(0, itemsPerPage - curation.related_lectures.slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage).length) 
                            }).map((_, emptyIndex) => (
                              <div key={`empty-${emptyIndex}`} style={{ visibility: 'hidden' }}>
                                <LectureCard style={{ pointerEvents: 'none' }}>
                                  <LectureCardImage>
                                    <LectureCardBg />
                                  </LectureCardImage>
                                </LectureCard>
                              </div>
                            ))}
                          </LectureGrid>
                        </SlidePage>
                      ))}
                    </SlideWrapper>
                  ) : (
                    // 한 페이지만 있을 때 (슬라이드 없음)
                    <LectureGrid>
                      {curation.related_lectures.map((lecture) => (
                        <LectureCard 
                          key={lecture.relation_id}
                          onClick={() => navigate(`/class/lecture/${lecture.lecture_id}`)}
                        >
                          <LectureCardImage>
                            <LectureCardBg style={{backgroundImage: lecture.thumbnail_url ? `url('${lecture.thumbnail_url}')` : 'none'}} />
                            <LectureCardOverlay>
                              <LectureCardContent>
                                <LectureCardTitle>{lecture.title}</LectureCardTitle>
                                <LectureCardSubtitle>{lecture.lecturer_info.name} · {lecture.lecturer_info.department}</LectureCardSubtitle>
                              </LectureCardContent>
                            </LectureCardOverlay>
                          </LectureCardImage>
                        </LectureCard>
                      ))}
                    </LectureGrid>
                  )}
                </SlideContainer>
              </Section>
            );
          })}

          {/* 기존 VISIT THE CLASSROOM 섹션 (큐레이션 데이터가 없을 경우만 표시) */}
          {homeCurations.length === 0 && (
          <Section>
            <SectionHeader>
              <SectionTitleGroup>
                <SectionTitle>입문자를 위한 스타터 커리큘럼</SectionTitle>
                <SectionCaption>의료 AI의 핵심만 모은 입문 커리큘럼으로 빠르고 쉽게 기초를 잡아드립니다.</SectionCaption>
              </SectionTitleGroup>
              <SectionTrailing>
                <PaginationButtons>
                  <PaginationContainer>
                    <PaginationContent>
                      <PaginationBtn 
                        onClick={() => handleClassroomPageChange('prev')}
                        disabled={classroomPage === 0}
                      >
                        <PaginationIcon isActive={classroomPage > 0}>
                          <img src="/images/left.svg" alt="Previous" />
                        </PaginationIcon>
                      </PaginationBtn>
                      <PaginationBtn 
                        onClick={() => handleClassroomPageChange('next')}
                        disabled={classroomPage >= lectureTotalPages - 1}
                      >
                        <PaginationIcon isRight isActive={classroomPage < lectureTotalPages - 1}>
                          <img src="/images/left.svg" alt="Next" />
                        </PaginationIcon>
                      </PaginationBtn>
                    </PaginationContent>
                  </PaginationContainer>
                </PaginationButtons>
              </SectionTrailing>
            </SectionHeader>
            <SlideContainer
              ref={classroomRef}
              onMouseDown={(e) => handleDragStart(e, 'classroom')}
              onMouseMove={(e) => handleDragMove(e, 'classroom')}
              onTouchStart={(e) => handleDragStart(e, 'classroom')}
              onTouchMove={(e) => handleDragMove(e, 'classroom')}
              onMouseUp={() => handleDragEnd('classroom')}
              onTouchEnd={() => handleDragEnd('classroom')}
              onMouseLeave={() => handleDragEnd('classroom')}
              style={{ 
                pointerEvents: classroomDrag.isDragging && classroomDrag.hasMoved ? 'none' : 'auto',
                userSelect: 'none'
              }}
            >
              {lectureLoading ? (
                // 스켈레톤 UI
                <LectureGrid>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <LectureCard 
                      key={`skeleton-${index}`}
                      style={{ pointerEvents: 'none' }}
                    >
                      <LectureCardImage>
                        <SkeletonBackground />
                        <LectureCardOverlay>
                          <LectureCardContent>
                            <SkeletonTitle />
                            <SkeletonSubtitle />
                          </LectureCardContent>
                        </LectureCardOverlay>
                      </LectureCardImage>
                    </LectureCard>
                  ))}
                </LectureGrid>
              ) : (
                <SlideWrapper style={{ 
                  transform: `translateX(calc(-${classroomPage * (100/lectureTotalPages)}% - ${classroomPage * 24}px + ${classroomDrag.isDragging ? classroomDrag.offset : 0}px))`,
                  transition: classroomDrag.isDragging ? 'none' : 'transform 0.6s ease',
                  width: `${lectureTotalPages * 100}%`
                }}>
                  {Array.from({ length: lectureTotalPages }).map((_, pageIndex) => (
                    <SlidePage key={pageIndex} style={{ width: `${100/lectureTotalPages}%` }}>
                      <LectureGrid>
                        {allLectures[pageIndex] ? (
                          allLectures[pageIndex].map((lecture) => (
                            <LectureCard 
                              key={lecture.id}
                              onClick={(e) => handleLectureCardClick(lecture.id, e)}
                            >
                              <LectureCardImage>
                                <LectureCardBg style={{backgroundImage: lecture.thumbnail_url ? `url('${String(lecture.thumbnail_url)}')` : 'none'}} />
                                <LectureCardOverlay>
                                  <LectureCardContent>
                                    <LectureCardTitle>{String(lecture.title || '')}</LectureCardTitle>
                                    <LectureCardSubtitle>{String(lecture.caption || '')}</LectureCardSubtitle>
                                  </LectureCardContent>
                                </LectureCardOverlay>
                              </LectureCardImage>
                            </LectureCard>
                          ))
                        ) : (
                          // 로딩 중이거나 데이터가 없을 때 스켈레톤 표시
                          Array.from({ length: 3 }).map((_, index) => (
                            <LectureCard key={`skeleton-${pageIndex}-${index}`} style={{ pointerEvents: 'none' }}>
                              <LectureCardImage>
                                <SkeletonBackground />
                                <LectureCardOverlay>
                                  <LectureCardContent>
                                    <SkeletonTitle />
                                    <SkeletonSubtitle />
                                  </LectureCardContent>
                                </LectureCardOverlay>
                              </LectureCardImage>
                            </LectureCard>
                          ))
                        )}
                      </LectureGrid>
                    </SlidePage>
                  ))}
                </SlideWrapper>
              )}
            </SlideContainer>
          </Section>
          )}

          {/* 멘토링 라운지 섹션 - 커피챗 API 데이터 사용 */}
          {coffeeChatSections.map((section, sectionIndex) => {
            const currentPage = coffeeChatSectionPages[sectionIndex] || 0;
            const itemsPerPage = 4; // 한 페이지에 4개씩
            const totalItems = section.related_persons.length;
            const totalPages = Math.ceil(totalItems / itemsPerPage);
            const sectionDrag = coffeeChatSectionDrags[sectionIndex] || {
              isDragging: false,
              startX: 0,
              currentX: 0,
              offset: 0,
              hasMoved: false,
              preventClick: false
            };
            
            return (
              <Section key={section.coffee_chat_section_id}>
                <SectionHeader>
                  <SectionTitleGroup>
                    <SectionTitle>{section.title}</SectionTitle>
                    <SectionCaption>{section.text}</SectionCaption>
                  </SectionTitleGroup>
                  {totalPages > 1 && (
                    <SectionTrailing>
                      <PaginationButtons>
                        <PaginationContainer>
                          <PaginationContent>
                            <PaginationBtn 
                              onClick={() => handleCoffeeChatSectionPageChange(sectionIndex, 'prev')}
                              disabled={currentPage === 0}
                            >
                              <PaginationIcon isActive={currentPage > 0}>
                                <img src="/images/left.svg" alt="Previous" />
                              </PaginationIcon>
                            </PaginationBtn>
                            <PaginationBtn 
                              onClick={() => handleCoffeeChatSectionPageChange(sectionIndex, 'next')}
                              disabled={currentPage >= totalPages - 1}
                            >
                              <PaginationIcon isRight isActive={currentPage < totalPages - 1}>
                                <img src="/images/left.svg" alt="Next" />
                              </PaginationIcon>
                            </PaginationBtn>
                          </PaginationContent>
                        </PaginationContainer>
                      </PaginationButtons>
                    </SectionTrailing>
                  )}
                </SectionHeader>
                <SlideContainer
                  ref={(el) => { coffeeChatSectionRefs.current[sectionIndex] = el; }}
                  onMouseDown={(e) => handleDragStart(e, `coffee-chat-section-${sectionIndex}`)}
                  onMouseMove={(e) => handleDragMove(e, `coffee-chat-section-${sectionIndex}`)}
                  onTouchStart={(e) => handleDragStart(e, `coffee-chat-section-${sectionIndex}`)}
                  onTouchMove={(e) => handleDragMove(e, `coffee-chat-section-${sectionIndex}`)}
                  onMouseUp={() => handleDragEnd(`coffee-chat-section-${sectionIndex}`)}
                  onTouchEnd={() => handleDragEnd(`coffee-chat-section-${sectionIndex}`)}
                  onMouseLeave={() => handleDragEnd(`coffee-chat-section-${sectionIndex}`)}
                  style={{ 
                    pointerEvents: sectionDrag.isDragging && sectionDrag.hasMoved ? 'none' : 'auto',
                    userSelect: 'none'
                  }}
                >
                  {coffeeChatLoading ? (
                    // 스켈레톤 UI
                    <CoffeeChatGrid>
                      {Array.from({ length: 4 }).map((_, index) => (
                        <CoffeeChatCard 
                          key={`skeleton-${index}`}
                          style={{ pointerEvents: 'none' }}
                        >
                          <CoffeeChatCardImage>
                            <SkeletonBackground />
                            <CoffeeChatCardOverlay>
                              <CoffeeChatCardContent>
                                <SkeletonTitle />
                                <SkeletonSubtitle />
                              </CoffeeChatCardContent>
                            </CoffeeChatCardOverlay>
                          </CoffeeChatCardImage>
                        </CoffeeChatCard>
                      ))}
                    </CoffeeChatGrid>
                  ) : totalPages > 1 ? (
                    <SlideWrapper style={{ 
                      transform: `translateX(calc(-${currentPage * (100/totalPages)}% - ${currentPage * 24}px + ${sectionDrag.isDragging ? sectionDrag.offset : 0}px))`,
                      transition: sectionDrag.isDragging ? 'none' : 'transform 0.6s ease',
                      width: `${totalPages * 100}%`
                    }}>
                      {Array.from({ length: totalPages }).map((_, pageIndex) => (
                        <SlidePage key={pageIndex} style={{ width: `${100/totalPages}%` }}>
                          <CoffeeChatGrid>
                            {section.related_persons
                              .slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage)
                              .map((person) => (
                                <CoffeeChatCard 
                                  key={person.section_related_person_id}
                                  onClick={(e) => handleCoffeeChatPersonClick(person.person_id, sectionIndex, e)}
                                >
                                  <CoffeeChatCardImage>
                                    <CoffeeChatCardBg 
                                      style={{backgroundImage: person.image_url ? `url('${person.image_url}')` : 'none'}} 
                                    />
                                    <CoffeeChatCardOverlay>
                                      <CoffeeChatCardContent>
                                        <CoffeeChatCardTitle>{person.name}</CoffeeChatCardTitle>
                                        <CoffeeChatCardSubtitle>{person.department}</CoffeeChatCardSubtitle>
                                        {person.tags && person.tags.length > 0 && (
                                          <CoffeeChatCardTags>
                                            {person.tags.join(', ')}
                                          </CoffeeChatCardTags>
                                        )}
                                      </CoffeeChatCardContent>
                                    </CoffeeChatCardOverlay>
                                  </CoffeeChatCardImage>
                                </CoffeeChatCard>
                              ))}
                            {/* 빈 공간 채우기 */}
                            {Array.from({ 
                              length: Math.max(0, itemsPerPage - section.related_persons.slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage).length) 
                            }).map((_, emptyIndex) => (
                              <div key={`empty-${emptyIndex}`} style={{ visibility: 'hidden' }}>
                                <CoffeeChatCard style={{ pointerEvents: 'none' }}>
                                  <CoffeeChatCardImage>
                                    <CoffeeChatCardBg />
                                  </CoffeeChatCardImage>
                                </CoffeeChatCard>
                              </div>
                            ))}
                          </CoffeeChatGrid>
                        </SlidePage>
                      ))}
                    </SlideWrapper>
                  ) : (
                    // 한 페이지만 있을 때 (슬라이드 없음)
                    <CoffeeChatGrid>
                      {section.related_persons.map((person) => (
                        <CoffeeChatCard 
                          key={person.section_related_person_id}
                          onClick={() => navigate(`/coffee-chat/${person.person_id}`)}
                        >
                          <CoffeeChatCardImage>
                            <CoffeeChatCardBg 
                              style={{backgroundImage: person.image_url ? `url('${person.image_url}')` : 'none'}} 
                            />
                            <CoffeeChatCardOverlay>
                              <CoffeeChatCardContent>
                                <CoffeeChatCardTitle>{person.name}</CoffeeChatCardTitle>
                                <CoffeeChatCardSubtitle>{person.department}</CoffeeChatCardSubtitle>
                                {person.tags && person.tags.length > 0 && (
                                  <CoffeeChatCardTags>
                                    {person.tags.join(', ')}
                                  </CoffeeChatCardTags>
                                )}
                              </CoffeeChatCardContent>
                            </CoffeeChatCardOverlay>
                          </CoffeeChatCardImage>
                        </CoffeeChatCard>
                      ))}
                    </CoffeeChatGrid>
                  )}
                </SlideContainer>
              </Section>
            );
          })}
        </MainContainer>
      </Container>
    </Layout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
`;

/* 배너 섹션 */
const BannerSection = styled.section`
  position: relative;
  width: 100vw;
  height: 480px;
  margin-left: calc(50% - 50vw);
  overflow: hidden;
  
  /* 폰: 고정 400px 은 화면 세로의 절반 가까이를 먹는다. 폭의 절반(2:1)으로 —
     운영 배너 그림(약 2.1:1)을 거의 자르지 않는다. */
  @media (max-width: 768px) {
    height: auto;
    aspect-ratio: 2 / 1;
  }
`;

const BannerSlideWrapper = styled.div`
  display: flex;
  height: 100%;
  transition: transform 0.6s ease;
  will-change: transform;
  cursor: grab;
  user-select: none;
  
  &:active {
    cursor: grabbing;
  }
`;

const BannerSlide = styled.div`
  flex-shrink: 0;
  height: 100%;
  display: flex;
  justify-content: center;
`;

const BannerSlideContent = styled.div<{ $backgroundImage?: string }>`
  width: 100%;
  height: 100%;
  background: ${props => props.$backgroundImage 
    ? `url('${props.$backgroundImage}') center/cover` 
    : `url('/images/banner-main.png') center/cover`
  };
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const BannerContent = styled.div`
  max-width: ${({ theme }) => theme.layout.containerWidth};
  width: 100%;
  padding: 0 ${({ theme }) => theme.layout.containerPadding};
  color: var(--Static-White, white);
  z-index: 2;
  
  @media (max-width: 1024px) {
    padding: 0 ${({ theme }) => theme.layout.containerPaddingTablet};
  }
  
  @media (max-width: 768px) {
    padding: 0 ${({ theme }) => theme.layout.containerPaddingMobile};
  }
`;

const BannerTitle = styled.h1<{ $fontColor?: string }>`
  color: ${props => props.$fontColor || 'var(--Static-White, white)'};
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 56px;
  font-weight: 700;
  line-height: 72.02px;
  word-wrap: break-word;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 41.6px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 31px;
    margin-bottom: 4px;
  }
`;

const BannerSubtitle = styled.h2<{ $fontColor?: string }>`
  color: ${props => props.$fontColor || 'var(--Static-White, white)'};
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 22px;
  font-weight: 400;
  line-height: 30.01px;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 24.6px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const BannerPagination = styled.div`
  position: absolute;
  bottom: 20px;

  @media (max-width: 768px) {
    bottom: 10px;
  }
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 3;
`;

const PaginationDot = styled.button<{ $active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.$active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: ${props => props.$active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.8)'};
  }
`;

/* 메인 컨테이너 */
const MainContainer = styled.div`
  max-width: ${({ theme }) => theme.layout.containerWidth};
  margin: 0 auto;
  padding: 64px ${({ theme }) => theme.layout.containerPadding} 128px;
  display: flex;
  flex-direction: column;
  gap: 64px;
  
  @media (max-width: 1024px) {
    padding: 64px ${({ theme }) => theme.layout.containerPaddingTablet} 128px;
  }
  
  @media (max-width: 768px) {
    padding: 40px ${({ theme }) => theme.layout.containerPaddingMobile} 100px;
    gap: 48px;
  }
`;

/* 섹션 공통 스타일 */
const Section = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const SectionHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const SectionTitleGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.h2`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.358;
  letter-spacing: -2.36%;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const SectionCaption = styled.p`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.445;
  letter-spacing: -0.02%;
  color: rgba(55, 56, 60, 0.61);
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SectionTrailing = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  height: 38px;
  
  @media (max-width: 768px) {
    align-self: flex-end;
  }
`;

/* 페이지네이션 버튼 */
const PaginationButtons = styled.div`
  height: 38px;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  display: inline-flex;
`;

const PaginationContainer = styled.div`
  position: relative;
  border-radius: 10px;
  outline: 1px rgba(112, 115, 124, 0.16) solid;
  outline-offset: -1px;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const PaginationContent = styled.div`
  overflow: hidden;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const PaginationBtn = styled.button<{ disabled?: boolean }>`
  padding: 7px;
  justify-content: center;
  align-items: center;
  display: flex;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  
  &:disabled {
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(23, 23, 25, 0.04);
      border-radius: 6px;
    }
  }
  
  &:first-child {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }
  
  &:last-child {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;

const PaginationIcon = styled.div<{ isRight?: boolean; isActive?: boolean }>`
  height: 18px;
  width: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  transform: ${props => props.isRight ? 'rotate(180deg)' : 'none'};
  
  img {
    width: 18px;
    height: 18px;
    filter: ${props => props.isActive ? 'brightness(0) saturate(100%) invert(0%)' : 'opacity(0.35)'};
    transition: filter 0.2s ease;
  }
`;

/* 슬라이드 애니메이션 컴포넌트 */
const SlideContainer = styled.div`
  width: 100%;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
  &:active {
    cursor: grabbing;
  }
  
  * {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  width: 300%; /* 3페이지 * 100% */
  transition: transform 0.6s ease;
  will-change: transform;
`;

const SlidePage = styled.div`
  width: calc(100% / 3); /* 전체 너비의 1/3 */
  flex-shrink: 0;
  
  &:not(:last-child) {
    margin-right: 24px;
  }
`;

/* 카테고리 카드 그리드 (6개, 1:1 비율) */
const CategoryGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const CategoryCard = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 10.2px;
  overflow: hidden;
  cursor: pointer;
  border: 0.8px solid rgba(112, 115, 124, 0.16);
`;

const CategoryCardImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  background-color: #f5f5f5; /* 기본 배경색 */
  transition: transform 0.3s ease; /* 기본 상태에 transition 적용 */
  
  ${CategoryCard}:hover & {
    transform: scale(1.05);
  }
`;

const CategoryCardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const CategoryCardTitle = styled.h3`
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.12), 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 0px 1px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

/* 강의 카드 그리드 (3개, 3:2 비율) */
const LectureGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const LectureCard = styled.div`
  border-radius: 10.2px;
  overflow: hidden;
  cursor: pointer;
  border: 0.8px solid rgba(112, 115, 124, 0.16);
`;

const LectureCardImage = styled.div`
  width: 100%;
  aspect-ratio: 3/2;
  position: relative;
  overflow: hidden;
`;

const LectureCardBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  transition: transform 0.3s ease; /* 기본 상태에 transition 적용 */
  
  ${LectureCard}:hover & {
    transform: scale(1.05);
  }
`;

const LectureCardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.61) 0%, transparent 120px);
  display: flex;
  align-items: flex-end;
  padding: 24px;
`;

const LectureCardContent = styled.div`
  color: white;
`;

const LectureCardTitle = styled.h3`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: -0.012em;
  text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.12), 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 0px 1px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const LectureCardSubtitle = styled.p`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.429;
  letter-spacing: 0.0145em;
  opacity: 0.9;
  text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.12), 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 0px 1px rgba(0, 0, 0, 0.08);
`;

/* 커피챗 카드 그리드 (4개, 1:1 비율) */
const CoffeeChatGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const CoffeeChatCard = styled.div`
  position: relative;
  aspect-ratio: 1;
  min-height: 200px; /* 최소 높이 설정 */
  border-radius: 10.2px;
  overflow: hidden;
  cursor: pointer;
  border: 0.8px solid rgba(112, 115, 124, 0.16);
`;

const CoffeeChatCardImage = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const CoffeeChatCardBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  background-color: #f5f5f5; /* 이미지 로드 전 배경색 */
  transition: transform 0.3s ease; /* 기본 상태에 transition 적용 */
  
  ${CoffeeChatCard}:hover & {
    transform: scale(1.05);
  }
`;

const CoffeeChatCardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.61) 0%, transparent 120px);
  display: flex;
  align-items: flex-end;
  padding: 24px;
`;

const CoffeeChatCardContent = styled.div`
  color: white;
`;

const CoffeeChatCardTitle = styled.h3`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: -0.012em;
  margin-bottom: 4px;
  text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.12), 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 0px 1px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CoffeeChatCardSubtitle = styled.p`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.429;
  letter-spacing: 0.0145em;
  opacity: 0.9;
  text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.12), 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 0px 1px rgba(0, 0, 0, 0.08);
`;

const CoffeeChatCardTags = styled.p`
  font-family: 'Pretendard JP', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.02em;
  opacity: 0.8;
  margin-top: 4px;
  text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.12), 0px 0px 1px rgba(0, 0, 0, 0.08), 0px 0px 1px rgba(0, 0, 0, 0.08);
`;

/* 스켈레톤 UI 컴포넌트들 */
const SkeletonBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonTitle = styled.div`
  width: 80%;
  height: 20px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  margin-bottom: 8px;
  animation: pulse 1.5s ease-in-out infinite alternate;
  
  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    100% {
      opacity: 0.8;
    }
  }
`;

const SkeletonSubtitle = styled.div`
  width: 60%;
  height: 14px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite alternate;
  animation-delay: 0.2s;
  
  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    100% {
      opacity: 0.8;
    }
  }
`;

export default Home;